import { supabase } from './supabase'

/**
 * Calls into the Cloudflare Pages Functions under /api.
 *
 * Anything that needs the service-role key, the ENCRYPTION_KEY, or scheduling
 * logic shared with the Worker lives behind these routes rather than in the
 * browser. Every call carries the admin's Supabase JWT, which the function
 * verifies before it does anything privileged.
 */

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

async function authHeader(): Promise<Record<string, string>> {
  const { data } = await supabase.auth.getSession()
  const token = data.session?.access_token
  return token ? { Authorization: `Bearer ${token}` } : {}
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(path, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(await authHeader()),
      ...(init.headers as Record<string, string> | undefined),
    },
  })

  const text = await res.text()
  const body = text ? safeJson(text) : null

  if (!res.ok) {
    throw new ApiError(errorMessage(body) ?? `Request failed (${res.status})`, res.status)
  }

  return body as T
}

/** Pulls the `error` string out of an API error body, if there is one. */
function errorMessage(body: unknown): string | null {
  if (body && typeof body === 'object' && 'error' in body) {
    const value = (body as { error: unknown }).error
    if (typeof value === 'string' && value) return value
  }
  return null
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text)
  } catch {
    return { raw: text }
  }
}

// --- queue -------------------------------------------------------------------

/** Take rows out of the queue for good. They never send. */
export function cancelQueueItems(ids: string[], reason?: string) {
  return request<{ canceled: number }>('/api/queue/cancel', {
    method: 'POST',
    body: JSON.stringify({ ids, reason }),
  })
}

export interface PauseResult {
  paused: boolean
  updated: number
  skipped: number
  rows: { id: string; scheduled_at: string | null }[]
}

/**
 * Hold scheduled rows, or let them go again. scheduled_at is untouched, so
 * resuming restores the original moment rather than restarting the delay.
 */
export function setQueuePaused(ids: string[], paused: boolean) {
  return request<PauseResult>('/api/queue/pause', {
    method: 'POST',
    body: JSON.stringify({ ids, paused }),
  })
}

/** The exact subject/text/html the Worker would send, unsubscribe link and all. */
export function previewQueueItem(id: string) {
  return request<{
    subject: string
    text: string
    html: string
    to: string
    from: string
    reply_to: string | null
  }>(`/api/queue/preview?id=${encodeURIComponent(id)}`)
}

// --- leads -------------------------------------------------------------------

export interface EnqueueResult {
  lead_id: string
  queue_id: string | null
  status: string
  scheduled_at: string | null
  message: string
}

/**
 * Adds a lead and queues it as 'scheduled' straight away, with the campaign's
 * delay applied from now.
 */
export function createLead(input: {
  campaign_id: string
  email: string
  name?: string
  fields?: Record<string, string>
  source?: string
}) {
  return request<EnqueueResult>('/api/leads', {
    method: 'POST',
    body: JSON.stringify(input),
  })
}

export function importLeads(input: {
  campaign_id: string
  rows: { email: string; name?: string; fields?: Record<string, string> }[]
  source?: string
}) {
  return request<{ inserted: number; queued: number; skipped: { email: string; reason: string }[] }>(
    '/api/leads/import',
    { method: 'POST', body: JSON.stringify(input) },
  )
}

// --- identities (SMTP accounts) ----------------------------------------------

export interface IdentityInput {
  id?: string
  label: string
  host: string
  port: number
  username: string
  /** Plaintext on the wire (HTTPS), encrypted before it touches the database. */
  password?: string
  from_name: string
  from_email: string
  reply_to?: string
  /** default {{sender_title}} for campaigns sending from this mailbox */
  sender_title?: string
  is_default?: boolean
}

export function saveIdentity(input: IdentityInput) {
  return request<{ id: string }>('/api/identities', {
    method: input.id ? 'PUT' : 'POST',
    body: JSON.stringify(input),
  })
}

export function deleteIdentity(id: string) {
  return request<{ deleted: true }>(`/api/identities?id=${encodeURIComponent(id)}`, {
    method: 'DELETE',
  })
}

/** Opens a real SMTP connection and authenticates. Never sends a message. */
export function testIdentity(id: string) {
  return request<{ ok: boolean; detail: string }>('/api/identities/test', {
    method: 'POST',
    body: JSON.stringify({ id }),
  })
}

// --- templates -----------------------------------------------------------------

/**
 * Sends the current draft to the signed-in admin's address via the real SMTP
 * path. While SEND_ENABLED is false the server reports a dry run instead.
 */
export function sendTestEmail(input: {
  subject: string
  body_html: string
  body_text: string
  body_mode?: 'rich' | 'html'
  campaign_id?: string
}) {
  return request<{ sent: boolean; detail: string; headers: string }>(
    '/api/templates/test-send',
    { method: 'POST', body: JSON.stringify(input) },
  )
}

// --- send engine ---------------------------------------------------------------

export interface TickResult {
  sent: number
  would_send: number
  failed: number
  skipped: number
  campaigns_considered: number
  dry_run: boolean
  errors: string[]
  sample_headers?: string
}

/**
 * Runs one tick of the same queue processor the cron Worker runs. Honours
 * SEND_ENABLED: dry-runs and reports "would send N" while the gate is off.
 */
export function processDueSendsNow() {
  return request<TickResult>('/api/send/run', { method: 'POST' })
}

// --- misc --------------------------------------------------------------------

export function addUnsubscribe(email: string, reason?: string) {
  return request<{ email: string }>('/api/unsubscribes', {
    method: 'POST',
    body: JSON.stringify({ email, reason }),
  })
}

export function removeUnsubscribe(email: string) {
  return request<{ deleted: true }>(`/api/unsubscribes?email=${encodeURIComponent(email)}`, {
    method: 'DELETE',
  })
}

/** Puts failed rows back in the queue with attempts reset. */
export function retryFailed(ids: string[]) {
  return request<{ retried: number }>('/api/queue/retry', {
    method: 'POST',
    body: JSON.stringify({ ids }),
  })
}

export interface SendConfig {
  send_enabled: boolean
  public_base_url: string
}

/** Reports whether SEND_ENABLED is on, so the UI can say so plainly. */
export function getSendConfig() {
  return request<SendConfig>('/api/config')
}
